import React, { useState, useEffect } from 'react';

const MilestoneCelebration = ({ milestone, lessonCount, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [confetti, setConfetti] = useState([]);

  useEffect(() => {
    // Trigger animation
    setTimeout(() => setIsVisible(true), 100);
    
    // Generate confetti
    const confettiArray = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 2,
      color: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A'][Math.floor(Math.random() * 5)]
    }));
    setConfetti(confettiArray);
    
    // Auto-close after 5 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease'
      }}
      onClick={handleClose}
    >
      {/* Confetti */}
      {confetti.map(c => (
        <div
          key={c.id}
          style={{
            position: 'absolute',
            top: '-10px',
            left: `${c.left}%`,
            width: '10px',
            height: '10px',
            background: c.color,
            borderRadius: '50%',
            animation: `confettiFall ${c.duration}s linear ${c.delay}s infinite`,
            opacity: 0.8
          }}
        />
      ))}

      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
          borderRadius: '32px',
          padding: '48px 32px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5)',
          transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(50px)',
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          color: 'white'
        }}
      >
        {/* Animated rings */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          border: '3px solid rgba(255, 255, 255, 0.2)',
          animation: 'pulse 2s infinite'
        }} />
        
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          animation: 'pulse 2s infinite 0.5s'
        }} />

        {/* Trophy icon */}
        <div style={{
          fontSize: '120px',
          marginBottom: '24px',
          animation: 'trophyBounce 1s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          filter: 'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3))'
        }}>
          🏆
        </div>

        {/* Title */}
        <h2 style={{
          margin: '0 0 16px 0',
          fontSize: '36px',
          fontWeight: '800',
          textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }}>
          Milestone Achieved!
        </h2>

        {/* Milestone number */}
        <div style={{
          fontSize: '72px',
          fontWeight: '900',
          margin: '24px 0',
          textShadow: '0 6px 30px rgba(0, 0, 0, 0.4)',
          background: 'linear-gradient(180deg, #FFD700 0%, #FFA500 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          {milestone}
        </div>

        {/* Subtitle */}
        <p style={{
          margin: '0 0 24px 0',
          fontSize: '24px',
          fontWeight: '600',
          opacity: 0.95
        }}>
          Lessons Completed!
        </p>

        {/* Progress info */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.15)',
          padding: '16px 24px',
          borderRadius: '16px',
          marginBottom: '32px',
          backdropFilter: 'blur(10px)'
        }}>
          <p style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600'
          }}>
            🎯 {lessonCount} total lessons
          </p>
          <p style={{
            margin: '8px 0 0 0',
            fontSize: '14px',
            opacity: 0.9
          }}>
            Next milestone: {milestone + 10} lessons
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            width: '100%',
            padding: '18px',
            background: 'rgba(255, 255, 255, 0.25)',
            color: 'white',
            border: '2px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '16px',
            fontSize: '18px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.2s',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.35)';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.25)';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          Amazing! Let's Continue 🚀
        </button>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes confettiFall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes trophyBounce {
          0% {
            opacity: 0;
            transform: scale(0) rotate(-180deg);
          }
          50% {
            transform: scale(1.2) rotate(10deg);
          }
          70% {
            transform: scale(0.9) rotate(-5deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.1);
            opacity: 0.1;
          }
        }
      `}</style>
    </div>
  );
};

export default MilestoneCelebration;